/**
 * 🧾 **저장하면 빠지는 QR 코드** — 인쇄된 종이는 회수할 수 없다 (2026-10-13).
 *
 * 서버는 「기본 목록(파일) + 운영자 목록」을 코드로 합친다(shared `mergeRedirects`). 그래서 줄을 지우고 저장하면:
 *  - 운영자가 **더한** 코드 → 사라진다. 그 QR 을 찍은 아이는 **홈으로** 간다(인쇄물이 끊긴다).
 *  - **기본 목록**의 코드 → 안 지워지고 처음 목적지로 돌아간다. 운영자는 지웠다고 믿는다.
 * 둘 다 묻지 않고 저장됐다. 저장 직전에 코드마다 무엇이 되는지를 말한다(지도 편집기 removedOnPublish 와 같은 생각).
 */
export interface QrRow {
  code: string;
  target: string;
}

export interface QrRemoved {
  code: string;
  /** 화면이 보여 준 기간에 찍힌 수 — 0 이면 아직 아무도 안 찍었다 */
  hits: number;
  /** 저장 뒤 이 코드로 오면 어디로 가나 — 기본 목록에 있으면 그 목적지, 없으면 홈 */
  after: string;
}

const key = (c: string) => c.trim().toLowerCase();

export function qrRemovedOnSave(
  loaded: readonly QrRow[],
  saving: readonly QrRow[],
  base: readonly QrRow[],
  hits: readonly { code: string; n: number }[],
): QrRemoved[] {
  const keep = new Set(saving.map((r) => key(r.code)));
  const baseTarget = new Map(base.map((b) => [key(b.code), b.target]));
  const hitOf = new Map(hits.map((h) => [key(h.code), h.n]));
  return loaded
    .filter((r) => key(r.code) && !keep.has(key(r.code)))
    .map((r) => ({ code: r.code.trim(), hits: hitOf.get(key(r.code)) ?? 0, after: baseTarget.get(key(r.code)) ?? "/" }))
    // 기본 목록 그대로였던 줄을 지운 것은 저장해도 바뀌는 게 없다 — 묻지 않는다
    .filter((x) => !(baseTarget.has(key(x.code)) && loaded.find((r) => key(r.code) === key(x.code))?.target === x.after));
}

/** 운영자에게 묻는 말 — 코드마다 한 줄. 기간 이름은 화면이 보여 준 그대로 */
export function qrRemovedSay(list: readonly QrRemoved[], periodLabel: string): string {
  const lines = list.map((x) => {
    const seen = x.hits > 0 ? `${periodLabel} ${x.hits}번 찍혔어요` : "찍힌 기록 없음";
    const goes = x.after === "/" ? "앞으로 찍으면 홈으로 가요" : `처음 설정 목적지(${x.after})로 돌아가요`;
    return `• ${x.code} — ${seen} → ${goes}`;
  });
  return `저장하면 이 QR 코드가 바뀌어요:\n\n${lines.join("\n")}\n\n인쇄된 종이는 회수할 수 없어요. 그래도 저장할까요?`;
}
